import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { reportTypeOptions } from './SidebarConstants';
import { useAuth } from 'react-oidc-context';
import api from '../api';

const Sidebar = () => {
    const { state, actions } = useFormContext();
    const auth = useAuth();
    const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';

    // Check if user is in 'admin' group
    const userGroups = auth.user?.profile?.['cognito:groups'] || [];
    const isAdmin = userGroups.includes('admin') || bypassAuth;

    // Filter report types based on admin status
    const availableReportTypes = reportTypeOptions.filter(option => {
        if (option === 'Impaction' && !isAdmin) return false;
        return true;
    });

    const patientInfoRef = useRef(null);
    const [units, setUnits] = useState(["UNIT Number"]);
    const [doctors, setDoctors] = useState(["Doctor Name"]);

    // Fetch Units and Doctors on mount
    useEffect(() => {
        const fetchData = async () => {
            console.log("Fetching sidebar data...");
            console.log("API Base URL:", api.defaults.baseURL);
            try {
                const [unitsRes, doctorsRes] = await Promise.all([
                    api.get('/units'),
                    api.get('/doctors')
                ]);

                console.log("Units Response:", unitsRes.data);
                console.log("Doctors Response:", doctorsRes.data);

                // Format Units: "UNIT-ID   ---  ADDRESS"
                // The 'name' field in DB for existing units already contains the "UNIT-X --- LOCATION" string.
                // We should NOT append the full address here, as it duplicates info or makes it too long.
                const formattedUnits = ["UNIT Number", ...unitsRes.data.map(u => u.name)];
                setUnits(formattedUnits);

                // Format Doctors: "Name"
                const formattedDoctors = ["Doctor Name", ...doctorsRes.data.map(d => d.name)];
                setDoctors(formattedDoctors);
            } catch (error) {
                console.error("Failed to fetch sidebar data:", error);
                if (error.response) {
                    console.error("Error Response Data:", error.response.data);
                    console.error("Error Status:", error.response.status);
                }
                actions.setError(`Failed to load sidebar data: ${error.message} (API: ${api.defaults.baseURL})`);
            }
        };

        fetchData();
    }, []);

    // Auto-scroll to patient info when data is loaded
    useEffect(() => {
        if (state.patientData && patientInfoRef.current) {
            patientInfoRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [state.patientData]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        actions.setSelectedFile(file);
        console.log('File selected:', file);
    };

    const handleUnitChange = (event) => {
        actions.setSelectedUnit(event.target.value);
        console.log('Unit selected:', event.target.value);
    };

    const handleDoctorChange = (event) => {
        actions.setSelectedDoctor(event.target.value);
        console.log('Doctor selected:', event.target.value);
    };

    const handleReportTypeChange = (event) => {
        actions.setReportType(event.target.value);
        console.log('Report type selected:', event.target.value);
    };

    const handleReadDetails = async () => {
        if (!state.selectedFile || state.selectedUnit === 'UNIT Number' || state.selectedDoctor === 'Doctor Name') {
            actions.setError('Please select a DICOM file, Unit, and Doctor.');
            return;
        }

        // Preserve selections in local variables before resetting the state
        const fileToProcess = state.selectedFile;
        const unit = state.selectedUnit;
        const doctor = state.selectedDoctor;
        const type = state.reportType;

        // 1. Reset the entire form state to clear all old data
        actions.resetForm();

        // 2. Wait for the state to update (optional, but good practice)
        await new Promise(resolve => setTimeout(resolve, 50));

        // 3. Restore the essential selections for the new report
        actions.setSelectedFile(fileToProcess);
        actions.setSelectedUnit(unit);
        actions.setSelectedDoctor(doctor);
        actions.setReportType(type);
        actions.setLoading(true);
        actions.clearError();

        // 4. Prepare and send the form data using the preserved variables
        const formData = new FormData();
        formData.append('file', fileToProcess);
        formData.append('unit', unit);
        formData.append('sign_doctor', doctor);

        try {
            const response = await api.post('/dicom/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            actions.setPatientData(response.data);
            actions.setCurrentTab(0); // Start with the first tab

        } catch (err) {
            console.error('Error uploading DICOM file:', err.response ? err.response.data : err.message);
            actions.setError('Failed to read patient details. Please try again.');
        } finally {
            actions.setLoading(false);
        }
    };

    const isReadDetailsDisabled = !state.selectedFile ||
        state.selectedUnit === 'UNIT Number' ||
        state.selectedDoctor === 'Doctor Name' ||
        state.loading;

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Fixed Logo Header - now positioned at the very top */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <img
                    src="/orald_logo.png"
                    alt="Orald3D Logo"
                    style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '90px'
                    }}
                />
            </Box>

            {/* Scrollable Content */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                },
            }}>
                <Typography variant="h6" gutterBottom>
                    Select Report Type
                </Typography>
                <Box mb={3}>
                    <FormControl fullWidth>
                        <InputLabel>Choose a report type</InputLabel>
                        <Select
                            value={state.reportType}
                            onChange={handleReportTypeChange}
                            label="Choose a report type"
                        >
                            {availableReportTypes.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Only show DICOM upload section for basic reports */}
                {state.reportType !== 'Virtual Implant' && (
                    <>
                        <Typography variant="h6" gutterBottom>
                            Select Patient's DICOM file
                        </Typography>

                        <Box mb={3}>
                            <Typography variant="subtitle1" gutterBottom>
                                Choose file location
                            </Typography>
                            <input
                                type="file"
                                onChange={handleFileSelect}
                                accept=".dcm"
                                style={{ marginBottom: '8px' }}
                            />
                            {state.selectedFile && (
                                <Typography variant="caption" display="block" color="success.main">
                                    Selected: {state.selectedFile.name}
                                </Typography>
                            )}
                        </Box>

                        <Box mb={3}>
                            <FormControl fullWidth>
                                <InputLabel>Select Unit:</InputLabel>
                                <Select
                                    value={state.selectedUnit}
                                    onChange={handleUnitChange}
                                    label="Select Unit:"
                                >
                                    {units.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box mb={3}>
                            <FormControl fullWidth>
                                <InputLabel>Verified By:</InputLabel>
                                <Select
                                    value={state.selectedDoctor}
                                    onChange={handleDoctorChange}
                                    label="Verified By:"
                                >
                                    {doctors.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleReadDetails}
                            fullWidth
                            disabled={isReadDetailsDisabled}
                            sx={{ mb: 2 }}
                        >
                            {state.loading ? 'Reading Details...' : 'Read Patient Details'}
                        </Button>

                        {/* Validation messages */}
                        {!state.selectedFile && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                Please select a DICOM file
                            </Typography>
                        )}
                        {state.selectedUnit === 'UNIT Number' && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                Please select a unit
                            </Typography>
                        )}
                        {state.selectedDoctor === 'Doctor Name' && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                Please select a doctor
                            </Typography>
                        )}
                    </>
                )}

                {/* Virtual Implant specific message */}
                {state.reportType === 'Virtual Implant' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.contrastText">
                            Virtual Implant Mode: Upload an existing basic CBCT report to add virtual implant planning.
                        </Typography>
                    </Box>
                )}

                {/* Patient Information Display */}
                {state.patientData && (
                    <Box ref={patientInfoRef} sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Patient Information
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Name:</strong> {state.patientData.PatientName}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Referring Physician:</strong> {state.patientData.ReferringPhysicianName}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Age:</strong> {state.patientData.Age}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Gender:</strong> {state.patientData.Gender}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Study Date:</strong> {state.patientData.Date}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Slice Thickness:</strong> {state.patientData.slicethickness}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>KVP:</strong> {state.patientData.kvp}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>X-Ray Tube Current:</strong> {state.patientData.current}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Branch Address:</strong> {state.patientData.BranchAddress}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Unit Number:</strong> {state.patientData.UnitNumber}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Verified By:</strong> {state.patientData.VerifiedBy}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Sidebar;